<template>
  <v-row dense justify="center" align="center">
    <v-col cols="12" md="12">
      <v-autocomplete
        v-model="selectedModel"
        :items="models"
        :loading="loading"
        :search-input.sync="searchInput"
        item-text="text"
        item-value="value"
        :label="label || $t('llm_config.llm_models_autocomplete.label')"
        :placeholder="
          placeholder || $t('llm_config.llm_models_autocomplete.placeholder')
        "
        :hint="hint"
        persistent-hint
        :rules="form_roles.text"
        prepend-inner-icon="mdi-robot"
        clearable
        outlined
        :elevation="0"
        required
        small-chips
        light
        hide-details="auto"
        :menu-props="{ bottom: true, offsetY: true }"
        :error="!!errorMessage"
        :error-count="errorMessage ? 1 : 0"
        :no-data-text="loading ? 'Loading models...' : 'No models found'"
        :error-messages="errorMessage"
        @change="onModelChange"
      >
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
            <v-list-item-subtitle>
              <span class="text-caption">Owner: {{ item.owned_by }}</span>
              <span class="text-caption" v-if="item.created">
                | Created: {{ formatDate(item.created) }}</span
              >
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <template v-slot:no-data>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>
                {{ loading ? "Loading models..." : "No models found" }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-autocomplete>
    </v-col>

    <v-col cols="12" md="12">
      <!-- Display selected model info -->
      <v-card v-if="showModelDetails && selectedModelInfo" outlined>
        <v-card-title class="text-h6 pb-2">
          {{ $t("llm_config.llm_models_autocomplete.model_details") }}
        </v-card-title>
        <v-card-text>
          <div><strong>ID:</strong> {{ selectedModelInfo.id }}</div>
          <div><strong>Owner:</strong> {{ selectedModelInfo.owned_by }}</div>
          <div>
            <strong>Created:</strong>
            {{ formatDate(selectedModelInfo.created) }}
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "OpenAIModelSelector",
  props: {
    value: {
      type: String,
      default: "",
    },
    apiBaseUrl: {
      type: String,
      default: "http://localhost:48080",
    },
    form_roles: {
      type: Object,
      required: true,
    },
    showModelDetails: {
      type: Boolean,
      default: false,
    },
    openaiModels: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    hint: {
      type: String,
    },
    allowedModels: {
      type: Array,
      default: () => [],
    },
    extraExternalModels: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      models: [],
      loading: false,
      searchInput: "",
      errorMessage: "",
      selectedModel: this.value,
    };
  },
  computed: {
    selectedModelInfo() {
      return this.models.find((model) => model.id === this.selectedModel);
    },
  },
  watch: {
    value(newVal) {
      this.selectedModel = newVal;
    },
    searchInput(val) {
      // Debounce search to avoid too many API calls
      if (val && val.length > 0) {
        this.debouncedSearch();
      }
    },
  },
  mounted() {
    // eliminate the models that are not in allowedModels if allowedModels is not empty
    if (this.allowedModels.length > 0) {
      this.models = this.openaiModels.filter((model) =>
        this.allowedModels.includes(model.id)
      );
      // add allowed models that are not present in openaiModels (e.g. Mistral or other external models)
      const foundIds = this.models.map((m) => m.id);
      this.allowedModels.forEach((modelId) => {
        if (!foundIds.includes(modelId)) {
          this.models.push({
            id: modelId,
            text: modelId,
            value: modelId,
            owned_by: "external",
          });
        }
      });
    } else {
      this.models = this.openaiModels;
    }

    // add extraExternalModels to models if they are not already present
    if (this.extraExternalModels.length > 0) {
      this.extraExternalModels.forEach((model) => {
        this.models.push(model);
      });
    }
  },
  methods: {
    onModelChange(modelId) {
      this.$emit("input", modelId);
      this.$emit("model-selected", this.selectedModelInfo);
    },

    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleDateString();
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        // You can implement additional filtering here if needed
        // For now, Vuetify's autocomplete handles the filtering
      }, 300);
    },
  },
};
</script>
